import type { TFunction } from 'i18next';
import type { MessageOrigin, MessageThread, MilestoneMessage } from '../types';

export type ThreadSide = 'person' | 'company';
export type ThreadType = MessageThread['threadType'];

/** The party a persona speaks for in a thread. */
export const sideOfRole = (role: string | null | undefined): ThreadSide | null =>
  role === 'person' ? 'person' : role === 'company' || role === 'builder' ? 'company' : null;

/**
 * Texts the platform writes in a party's name when they act without a comment
 * (approving, withdrawing an objection, starting conflict mode). Nobody wrote
 * them, so the conversation leaves them out, as the API does (ClickUp 869fegbvk).
 */
const AUTOMATIC_TEXTS = [
  /^Milestone (approved|rejected)\.$/,
  // The API now shows the stored legacy texts in Swedish (ClickUp 869fm5cgn).
  /^Etappmålet (attesterades|fick en invändning)\.$/,
  /^(Invändningen|Bestridandet) återkallades och etappen godkändes\.$/,
  /^Konfliktläge startades för .+\.$/,
];
/** Contract thread entries a party wrote; the rest are status events. */
const CONTRACT_COMMENT_ACTIONS = new Set(['comment', 'reject', 'rejected', 'counterproposal', 'approve', 'approved']);

export const isConversationMessage = (message: MilestoneMessage, threadType: ThreadType = 'milestone') => {
  if (!sideOfRole(message.userRole)) return false;
  if (message.draftAction && (threadType !== 'contract' || !CONTRACT_COMMENT_ACTIONS.has(message.draftAction))) return false;
  // A project's own thread also logs its status changes.
  if (threadType === 'project' && (message.toStatus || message.fromStatus)) return false;
  return !AUTOMATIC_TEXTS.some(pattern => pattern.test(message.message.trim()));
};

/** The label of where a message was written, e.g. "Fråga vid attestering". */
export const originOf = (message: Pick<MilestoneMessage, 'origin' | 'attestationAction'>): MessageOrigin =>
  message.origin ?? (message.attestationAction === 'approved' ? 'approval'
    : message.attestationAction === 'rejected' ? 'objection' : 'message');

export type ThreadStateKey = 'unread' | 'read' | 'sent' | 'idle';
export type ThreadTone = 'new' | 'info' | 'muted';

/**
 * One line about where a thread stands, on the stage card: "Nytt
 * meddelande", "Läst" or "Skickat". There is no "Kräver svar" any more
 * (ClickUp 869fj3q97); unread messages are what asks for attention.
 */
export function threadState(thread: MessageThread, mySide: ThreadSide): { key: ThreadStateKey; tone: ThreadTone } {
  if (thread.unreadCount > 0) return { key: 'unread', tone: 'new' };
  if (thread.lastMessage?.side === mySide) {
    const read = !!thread.counterpartReadAt
      && new Date(thread.counterpartReadAt).getTime() >= new Date(thread.lastMessage.createdAt).getTime();
    return { key: read ? 'read' : 'sent', tone: 'info' };
  }
  return { key: 'idle', tone: 'muted' };
}

/** When a thread last saw activity: its latest message, or when the stage started (ClickUp 869fm3fy8). */
export const threadActivityAt = (thread: Pick<MessageThread, 'lastActivityAt' | 'lastMessage'>) =>
  thread.lastActivityAt ?? thread.lastMessage?.createdAt ?? '';

/** Under Avslutade: a paid-out stage or a completed project (ClickUp 869fm3fy8). */
export const isFinishedThread = (thread: Pick<MessageThread, 'finished' | 'projectCompleted'>) =>
  thread.finished ?? !!thread.projectCompleted;

/** A subject's key in a conversation: its thread type and id. */
export const subjectKey = (subject: { threadType: ThreadType; threadId: string }) => `${subject.threadType}:${subject.threadId}`;

/** What a thread is about: "Etappmål #2 · Plattsättning", "ÄTA #2 · …" or "Allmänt om projektet". */
export const subjectLabel = (thread: MessageThread, t: TFunction) => {
  if (thread.threadType === 'project') return t('messageThreads.general');
  if (thread.threadType === 'contract') {
    return t(thread.contractType === 'variation' ? 'messageThreads.variation' : 'messageThreads.contract', {
      number: thread.contractNumber, description: thread.contractName
    });
  }
  return t('messageThreads.stage', { number: thread.milestoneNumber, description: thread.milestoneDescription });
};

export const subjectIcon = (threadType: ThreadType) =>
  threadType === 'contract' ? 'file-text' : threadType === 'project' ? 'folder' : 'flag';

/** The subject's own page in the project: the stage dialog, the contract or the project. */
export const subjectPath = (thread: Pick<MessageThread, 'threadType' | 'projectId' | 'milestoneId' | 'contractId'>, mySide: ThreadSide) => {
  const base = `${mySide === 'person' ? '/person/projects' : '/projects'}/${thread.projectId}`;
  if (thread.threadType === 'contract') return `${base}?tab=contract&contractId=${encodeURIComponent(thread.contractId ?? '')}`;
  if (thread.threadType === 'project') return base;
  return `${base}?tab=timeline&milestoneId=${encodeURIComponent(thread.milestoneId ?? '')}&action=reply`;
};

/** The conversation with the thread's counterpart on Meddelanden, opened on that subject. */
export const threadPath = (thread: Pick<MessageThread, 'threadType' | 'threadId' | 'counterpartKey'>) =>
  `/messages?with=${encodeURIComponent(thread.counterpartKey)}&about=${encodeURIComponent(subjectKey(thread))}`;

export const sameThread = (thread: Pick<MessageThread, 'threadType' | 'threadId'>, type: ThreadType, id: string) =>
  thread.threadType === type && thread.threadId === id;

/** A time in Meddelanden, e.g. "26 okt. 13:25" (sv) or "26 Oct, 13:25" (en). */
export const formatMessageTime = (value: string, language: string) =>
  new Date(value).toLocaleString(language === 'en' ? 'en-GB' : 'sv-SE', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
  });

/**
 * Why the company cannot send another attestation reminder yet: the customer
 * has not read the previous one and it is less than 24 hours old (ClickUp
 * 869fmfkyz). Without the dates (an older API) the text leaves them out.
 */
export const attestationRequestPendingText = (
  t: TFunction,
  language: string,
  request: { milestoneNumber: number | null | undefined; latestRequestAt?: string | null; nextRequestAllowedAt?: string | null }
) => {
  const number = request.milestoneNumber ?? '';
  if (!request.latestRequestAt || !request.nextRequestAllowedAt) {
    return t('messageThreads.attestationRequest.unreadNoDate', { number });
  }
  return t('messageThreads.attestationRequest.unread', {
    number,
    sentAt: formatMessageTime(request.latestRequestAt, language),
    nextAt: formatMessageTime(request.nextRequestAllowedAt, language),
  });
};
